import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Filter, Grid, List, Plus, Upload, Download, Sparkles, Archive, Tag, MoreVertical, Image, ChevronDown } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function CatalogManagerPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ category: '', status: '', priceMin: '', priceMax: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ title: '', price: '', category: 'Electronics', sku: '', description: '' });
  const [enriching, setEnriching] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { loadData(); }, [page, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profile, res] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/v1/products', { params: { page, limit: 24, search, ...filters } })
      ]);
      setUser(profile.member);
      setProducts(res.data.products || res.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load:', err);
      setProducts([
        { id: 'p1', title: 'Premium Wireless Headphones', price: 149.99, category: 'Electronics', sku: 'WH-100-BLK', status: 'active', inventory: 234, ci_score: 850, image_url: null },
        { id: 'p2', title: 'Organic Cotton T-Shirt', price: 29.99, category: 'Apparel', sku: 'TS-OCT-M-BLU', status: 'active', inventory: 567, ci_score: 720, image_url: null },
        { id: 'p3', title: 'Smart Home Hub Pro', price: 199.99, category: 'Electronics', sku: 'SH-HUB-PRO', status: 'draft', inventory: 45, ci_score: 910, image_url: null },
        { id: 'p4', title: 'Artisan Coffee Blend', price: 18.99, category: 'Grocery', sku: 'CF-ART-12OZ', status: 'active', inventory: 1200, ci_score: 680, image_url: null },
        { id: 'p5', title: 'Running Shoes Ultra', price: 129.99, category: 'Shoes', sku: 'RS-ULT-10-BLK', status: 'active', inventory: 89, ci_score: 790, image_url: null },
        { id: 'p6', title: 'Bamboo Cutting Board Set', price: 34.99, category: 'Home goods', sku: 'HK-BCB-3PC', status: 'archived', inventory: 0, ci_score: 450, image_url: null },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async () => {
    try {
      const res = await apiClient.post('/api/v1/products', { ...newProduct, price: parseFloat(newProduct.price) });
      toast.success('Product created!');
      setShowAddProduct(false);
      setNewProduct({ title: '', price: '', category: 'Electronics', sku: '', description: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to create product');
    }
  };

  const enrichProducts = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select products first'); return; }
    setEnriching('bulk');
    try {
      for (const id of ids) {
        await apiClient.post(`/api/v1/products/${id}/enrich`, { fields: ['description', 'seo', 'attributes'] });
      }
      toast.success(`AI enrichment queued for ${ids.length} products`);
    } catch (err) {
      toast.error('Enrichment failed');
    } finally {
      setEnriching(null);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map(p => p.id)));
  };

  const getCIColor = (score) => {
    if (score >= 800) return 'text-[#3EF1C7]';
    if (score >= 600) return 'text-[#8CF78E]';
    if (score >= 300) return 'text-[#FFC857]';
    return 'text-[#F55454]';
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-[#3EF1C7]/10 text-[#3EF1C7]';
    if (s === 'draft') return 'bg-[#FFC857]/10 text-[#FFC857]';
    return 'bg-[#9CB0C7]/10 text-[#9CB0C7]';
  };

  const filtered = products.filter(p => {
    if (search && !p.title.toLowerCase().includes(search.toLowerCase()) && !p.sku?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Catalog Manager</h1>
            <p className="text-sm text-[#9CB0C7]">Catalog Manager</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowAddProduct(true)} className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#6B4FE0] transition">
              <Plus className="w-4 h-4" /> Add Product
            </button>
            <button className="flex items-center gap-2 px-3 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">
              <Upload className="w-4 h-4" /> Import
            </button>
            <button className="flex items-center gap-2 px-3 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Search & Filters Bar */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CB0C7]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products by name or SKU..." className="w-full bg-[#0E1426] border border-[#22304A] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm transition ${showFilters ? 'border-[#7A5CFF] text-[#7A5CFF]' : 'border-[#22304A] text-[#9CB0C7] hover:text-white'}`}>
            <Filter className="w-4 h-4" /> Filters
          </button>
          <div className="flex border border-[#22304A] rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={`p-2.5 ${viewMode === 'grid' ? 'bg-[#7A5CFF]/20 text-[#7A5CFF]' : 'text-[#9CB0C7]'}`}><Grid className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('list')} className={`p-2.5 ${viewMode === 'list' ? 'bg-[#7A5CFF]/20 text-[#7A5CFF]' : 'text-[#9CB0C7]'}`}><List className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 mb-4 bg-[#7A5CFF]/10 border border-[#7A5CFF]/30 rounded-lg px-4 py-3">
            <span className="text-sm text-[#7A5CFF] font-medium">{selected.size} selected</span>
            <button onClick={enrichProducts} disabled={enriching} className="flex items-center gap-1 px-3 py-1.5 bg-[#7A5CFF] text-white rounded text-xs font-medium">
              <Sparkles className="w-3 h-3" /> {enriching ? 'Enriching...' : 'AI Enrich'}
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 border border-[#22304A] text-[#9CB0C7] rounded text-xs"><Archive className="w-3 h-3" /> Archive</button>
            <button className="flex items-center gap-1 px-3 py-1.5 border border-[#22304A] text-[#9CB0C7] rounded text-xs"><Tag className="w-3 h-3" /> Bulk Price</button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-[#9CB0C7] hover:text-white">Clear</button>
          </div>
        )}

        {/* Products Grid */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(p => (
              <div key={p.id} onClick={() => toggleSelect(p.id)} className={`bg-[#0E1426] border rounded-xl p-4 cursor-pointer transition hover:border-[#7A5CFF]/50 ${selected.has(p.id) ? 'border-[#7A5CFF]' : 'border-[#22304A]'}`}>
                <div className="w-full h-32 bg-[#0B1120] rounded-lg mb-3 flex items-center justify-center">
                  {p.image_url ? <img src={p.image_url} alt={p.title} className="h-full object-contain" /> : <Image className="w-8 h-8 text-[#22304A]" />}
                </div>
                <h4 className="text-white text-sm font-medium mb-1 line-clamp-2">{p.title}</h4>
                <p className="text-xs text-[#9CB0C7] mb-2">SKU: {p.sku}</p>
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold">${p.price}</span>
                  <span className={`text-xs font-mono ${getCIColor(p.ci_score)}`}>CI:{p.ci_score}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColor(p.status)}`}>{p.status}</span>
                  <span className="text-xs text-[#9CB0C7]">{p.inventory} in stock</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3"><input type="checkbox" onChange={selectAll} checked={selected.size === products.length && products.length > 0} className="rounded" /></th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Product</th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">SKU</th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Category</th>
                  <th className="text-right p-3 text-xs text-[#9CB0C7] font-medium">Price</th>
                  <th className="text-right p-3 text-xs text-[#9CB0C7] font-medium">Inventory</th>
                  <th className="text-right p-3 text-xs text-[#9CB0C7] font-medium">CI Score</th>
                  <th className="text-center p-3 text-xs text-[#9CB0C7] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} onClick={() => toggleSelect(p.id)} className={`border-b border-[#22304A]/50 cursor-pointer hover:bg-[#7A5CFF]/5 ${selected.has(p.id) ? 'bg-[#7A5CFF]/10' : ''}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.has(p.id)} readOnly className="rounded" /></td>
                    <td className="p-3 text-sm text-white font-medium">{p.title}</td>
                    <td className="p-3 text-xs text-[#9CB0C7] font-mono">{p.sku}</td>
                    <td className="p-3 text-xs text-[#9CB0C7]">{p.category}</td>
                    <td className="p-3 text-sm text-white text-right">${p.price}</td>
                    <td className="p-3 text-sm text-right"><span className={p.inventory < 50 ? 'text-[#F55454]' : 'text-white'}>{p.inventory}</span></td>
                    <td className={`p-3 text-sm text-right font-mono ${getCIColor(p.ci_score)}`}>{p.ci_score}</td>
                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusColor(p.status)}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Product Modal */}
        {showAddProduct && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAddProduct(false)}>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Quick Add Product</h3>
              <div className="space-y-3">
                <input value={newProduct.title} onChange={e => setNewProduct({ ...newProduct, title: e.target.value })} placeholder="Product Title" className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} placeholder="Price" type="number" className="bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                  <input value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} placeholder="SKU" className="bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <select value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm">
                  {['Electronics', 'Apparel', 'Home goods', 'Beauty', 'Grocery', 'Shoes', 'Fitness', 'Outdoors', 'Baby', 'Gadgets'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <textarea value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} placeholder="Description (optional - AI can generate)" rows={3} className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                <div className="flex gap-3 pt-2">
                  <button onClick={addProduct} className="flex-1 py-2 bg-[#7A5CFF] text-white rounded-lg font-medium">Create Product</button>
                  <button onClick={() => setShowAddProduct(false)} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
